// UpgradePlan.js
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Box, Typography, Button, Paper, CircularProgress } from '@mui/material';

const MAX_PLAN_ID = 4;

export default function UpgradePlan() {
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [userRole, setUserRole] = useState("");
  const [teamName, setTeamName] = useState("");
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState(null);

  const fetchSubscriptionData = useCallback((isUpdate = false) => {
    if (isUpdate) setIsUpdating(true);
    else setIsInitialLoading(true);

    setTimeout(() => {
      try {
        const userStr = localStorage.getItem("user");
        if (userStr) {
          const userData = JSON.parse(userStr);

          setUserRole(userData?.active_team?.user_role_in_team || "");
          setTeamName(userData?.active_team?.team_name || "Unnamed Team");

          if (userData?.active_team?.subscription?.plan) {
            setSubscriptionData(userData.active_team.subscription);
          } else {
            setSubscriptionData({ plan: { name: "Freemium", id: null } });
          }
        } else {
          setSubscriptionData({ plan: { name: "Freemium", id: null } });
        }
      } catch (e) {
        console.error("UpgradePlan: Error parsing user data:", e);
        setError("Could not load your subscription information.");
        setSubscriptionData(null);
      } finally {
        if (isUpdate) setIsUpdating(false);
        else setIsInitialLoading(false);
      }
    }, 0);
  }, []);

  useEffect(() => {
    document.title = "Your Plan | KlonIT Web Application";
    fetchSubscriptionData(false);

    const handleLocalStorageUpdate = () => fetchSubscriptionData(true);
    window.addEventListener("userLocalStorageUpdated", handleLocalStorageUpdate);

    const handleStorageEvent = (event) => {
      if (event.key === "user" && event.storageArea === localStorage) {
        fetchSubscriptionData(true);
      }
    };
    window.addEventListener("storage", handleStorageEvent);

    return () => {
      window.removeEventListener("userLocalStorageUpdated", handleLocalStorageUpdate);
      window.removeEventListener("storage", handleStorageEvent);
    };
  }, [fetchSubscriptionData]);

  if (isInitialLoading) {
    return (
      <Box sx={{ 
        display: "flex", 
        justifyContent: "center", 
        alignItems: "center", 
        p: 3, 
        minHeight: "150px",
        backgroundColor: 'transparent',
      }}>
        <CircularProgress 
          size={24}
          sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
        />
      </Box>
    );
  }

  if (error) {
    return (
      <Paper 
        elevation={0}
        sx={{ 
          p: 2, 
          m: 2, 
          bgcolor: 'rgba(239, 68, 68, 0.15)',
          color: '#fca5a5',
          borderRadius: 2,
        }}
      >
        <Typography variant="body2">Error: {error}</Typography>
      </Paper>
    );
  }

  if (!subscriptionData || !subscriptionData.plan) {
    return (
      <Paper 
        elevation={0}
        sx={{ 
          p: 2, 
          m: 2,
          bgcolor: 'transparent',
          color: 'rgba(255, 255, 255, 0.8)',
          borderRadius: 2,
        }}
      >
        <Typography variant="body2">Could not determine your current plan.</Typography>
      </Paper>
    );
  }

  let currentPlanName = subscriptionData.plan.name || "Freemium";
  currentPlanName = currentPlanName.charAt(0).toUpperCase() + currentPlanName.slice(1);

  const currentPlanId = subscriptionData.plan.id || 0;
  const isOnMaxPlan = currentPlanId !== null && currentPlanId === MAX_PLAN_ID;

  return (
    <Box sx={{ p: 2 }}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          textAlign: "center",
          borderRadius: 2,
          backgroundColor: 'transparent',
          border: `1px solid rgba(255, 255, 255, 0.15)`,
          position: "relative",
          minHeight: "150px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {isUpdating && (
          <CircularProgress 
            size={24} 
            sx={{ 
              position: "absolute", 
              top: 16, 
              right: 16,
              color: 'rgba(255, 255, 255, 0.7)' 
            }} 
          />
        )}

        {/* Show Team Name & Role */}
        <Typography 
          variant="subtitle1" 
          gutterBottom
          sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
        >
          Team: <strong style={{ color: '#ffffff' }}>{teamName}</strong>
        </Typography>
        <Typography 
          variant="subtitle2" 
          gutterBottom
          sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
        >
          Role: <strong style={{ color: '#ffffff' }}>{userRole}</strong>
        </Typography>

        <Typography 
          variant="h6" 
          gutterBottom
          sx={{ color: 'rgba(255, 255, 255, 0.9)' }}
        >
          Your Current Plan:{" "}
          <strong style={{ color: '#3b82f6' }}>
            {currentPlanName}
          </strong>
        </Typography>

        {/* If Admin → show subscription controls, else view only */}
        {userRole.toLowerCase() === "admin" ? (
          isOnMaxPlan ? (
            <Typography 
              variant="body1" 
              sx={{ 
                mt: 2,
                color: 'rgba(255, 255, 255, 0.8)' 
              }}
            >
              You are already on our highest available plan. Thank you!
            </Typography>
          ) : (
            <>
              <Typography 
                variant="body1" 
                sx={{ 
                  mt: 1, 
                  mb: 2,
                  color: 'rgba(255, 255, 255, 0.8)' 
                }}
              >
                {currentPlanName === "Freemium"
                  ? "Upgrade to unlock premium features and benefits!"
                  : "Explore higher tiers for even more capabilities."}
              </Typography>
              <Button
                variant="contained"
                component={Link}
                to="/billing?tab=plan"
                sx={{ 
                  mt: 1, 
                  minWidth: "200px",
                  backgroundColor: '#3b82f6',
                  color: '#ffffff',
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: '#2563eb',
                  }
                }}
                disabled={isUpdating}
              >
                {isUpdating ? "Loading..." : "View Plans & Upgrade"}
              </Button>
            </>
          )
        ) : (
          <Typography 
            variant="body2" 
            sx={{ 
              mt: 2, 
              fontStyle: "italic",
              color: 'rgba(255, 255, 255, 0.7)' 
            }}
          >
            Only Admins can manage subscription. You can view team details.
          </Typography>
        )}
      </Paper>
    </Box>
  );
}